import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  crmApi, SERVICE_TYPE_LABELS, SERVICE_TYPE_COLORS, productsApi, tagsApi,
  operatorsApi, CrmOperator, authAxios,
} from '../services/crmApi';
import {
  Search, AlertTriangle, CheckCircle, TrendingUp, MessageSquare, Plus,
  FileSpreadsheet, BarChart3, Columns3, Calendar, X, CheckSquare, Square,
  Archive, RotateCcw, UserPlus, Mail, Send, Tag, Bookmark, Clock, Smartphone, CalendarRange, Share2, Trash2,
} from 'lucide-react';
import { AIAssistantModal } from './AIAssistantModal';
import { CRMDashboard } from './CRMDashboard';
import { MeetingsCalendar } from './MeetingsCalendar';
import { useToast } from './Toast';
import { useProductStages, PipelineStage } from '../hooks/useProducts';
import { KanbanColumn } from './kanban/KanbanColumn';
import { KanbanListView } from './kanban/KanbanListView';
import { QuickAddApplicationModal } from './kanban/QuickAddApplicationModal';
import { getProgramByServiceType, DEFAULT_TERMINOLOGY } from '../config/programs';
import { useIsMobile } from '../hooks/useIsMobile';
import { useYearContext } from '../contexts/YearContext';
import { useCurrentProduct } from '../contexts/ProductContext';
import { ArchiveSummary } from './ArchiveSummary';

interface UniversalKanbanProps {
  productCode?: string;
}

export const UniversalKanban: React.FC<UniversalKanbanProps> = ({ productCode: propCode }) => {
  const ctx = useCurrentProduct();
  const productCode = propCode || ctx.productCode;
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedYear, setSelectedYear, showArchive, setShowArchive } = useYearContext();
  const currentYearConst = new Date().getFullYear();

  const { stages, loading: stagesLoading } = useProductStages(productCode);

  // Persist filters in localStorage per product
  const storageKey = `kanban_filters_${productCode}`;
  const savedFilters = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}'); } catch { return {}; }
  }, [storageKey]);

  const [applications, setApplications] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(savedFilters.search || '');
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter || '');
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [viewMode, setViewMode] = useState<'dashboard' | 'kanban' | 'calendar'>(savedFilters.viewMode || 'kanban');
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [operators, setOperators] = useState<CrmOperator[]>([]);
  const [assignedFilter, setAssignedFilter] = useState<string>(savedFilters.assignedFilter || '');
  const [priorityFilter, setPriorityFilter] = useState<string>(savedFilters.priorityFilter || '');
  const [tagFilter, setTagFilter] = useState<string>(savedFilters.tagFilter || '');
  const [archivedCount, setArchivedCount] = useState(0);
  const [availableYears, setAvailableYears] = useState<{ year: number; total: number; active: number; archived: number }[]>([]);
  const [searchAllYears, setSearchAllYears] = useState(false);

  // Save filters to localStorage on change
  useEffect(() => {
    const filters: Record<string, string> = {};
    if (search) filters.search = search;
    if (statusFilter) filters.statusFilter = statusFilter;
    if (assignedFilter) filters.assignedFilter = assignedFilter;
    if (priorityFilter) filters.priorityFilter = priorityFilter;
    if (tagFilter) filters.tagFilter = tagFilter;
    if (viewMode !== 'kanban') filters.viewMode = viewMode;
    localStorage.setItem(storageKey, JSON.stringify(filters));
  }, [search, statusFilter, assignedFilter, priorityFilter, viewMode, storageKey]);

  const isMobile = useIsMobile();

  // Products with dedicated public application forms
  const HAS_DEDICATED_FORM = ['pup_grant', 'wsparcie_na_starcie', 'kfs'];

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailHtml, setBulkEmailHtml] = useState('');
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  const [showBulkSms, setShowBulkSms] = useState(false);
  const [bulkSmsMessage, setBulkSmsMessage] = useState('');
  const [sendingBulkSms, setSendingBulkSms] = useState(false);
  const [allTags, setAllTags] = useState<any[]>([]);
  const [showSaveView, setShowSaveView] = useState(false);
  const [viewName, setViewName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // WIP limits per column (localStorage)
  const wipKey = `kanban_wip_${productCode}`;
  const [wipLimits, setWipLimits] = useState<Record<string, number>>(() => {
    try { return JSON.parse(localStorage.getItem(wipKey) || '{}'); } catch { return {}; }
  });
  const [showWipConfig, setShowWipConfig] = useState(false);
  const updateWipLimit = (stageCode: string, limit: number) => {
    const next = { ...wipLimits, [stageCode]: limit };
    if (limit <= 0) delete next[stageCode];
    setWipLimits(next);
    localStorage.setItem(wipKey, JSON.stringify(next));
  };

  // Saved views (DB-backed)
  const [savedViews, setSavedViews] = useState<{ id?: number; name: string; filters: Record<string, string>; is_shared?: boolean }[]>([]);
  const fetchSavedViews = async () => {
    try {
      const views = await crmApi.getSavedViews(productCode);
      setSavedViews(views);
    } catch { /* ignore */ }
  };
  useEffect(() => { fetchSavedViews(); }, [productCode]);
  const handleSaveView = async () => {
    if (!viewName.trim()) return;
    const filters: Record<string, string> = {};
    if (search) filters.search = search;
    if (statusFilter) filters.statusFilter = statusFilter;
    if (assignedFilter) filters.assignedFilter = assignedFilter;
    if (priorityFilter) filters.priorityFilter = priorityFilter;
    if (tagFilter) filters.tagFilter = tagFilter;
    if (dateFrom) filters.dateFrom = dateFrom;
    if (dateTo) filters.dateTo = dateTo;
    try {
      await crmApi.saveView(viewName.trim(), filters, productCode);
      await fetchSavedViews();
      setViewName('');
      setShowSaveView(false);
      toast.success(`Widok "${viewName.trim()}" zapisany`);
    } catch { toast.error('Nie udało się zapisać widoku'); }
  };
  const handleLoadView = (view: { name: string; filters: Record<string, string> }) => {
    setSearch(view.filters.search || '');
    setStatusFilter(view.filters.statusFilter || '');
    setAssignedFilter(view.filters.assignedFilter || '');
    setPriorityFilter(view.filters.priorityFilter || '');
    setTagFilter(view.filters.tagFilter || '');
    setDateFrom(view.filters.dateFrom || '');
    setDateTo(view.filters.dateTo || '');
  };
  const handleDeleteView = async (id: number | undefined, name: string) => {
    if (id) {
      try { await crmApi.deleteView(id); await fetchSavedViews(); } catch { /* ignore */ }
    } else {
      setSavedViews(savedViews.filter(v => v.name !== name));
    }
  };

  // Drag and drop
  const [draggedApp, setDraggedApp] = useState<number | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);

  // Product config from programs.ts (fallback)
  const programConfig = getProgramByServiceType(productCode);
  const statusField = programConfig?.kanbanStatusField || 'status';
  const gradient = programConfig?.gradient || 'from-blue-600 to-indigo-600';
  const productLabel = programConfig?.label || productCode;
  const applyRoute = programConfig?.applyRoute || '/crm/apply';
  const isLoanProduct = statusField === 'loan_status';
  const isFinancialProduct = !!(programConfig?.features?.businessPlan || programConfig?.features?.loanInfo);
  const terminology = programConfig?.terminology || DEFAULT_TERMINOLOGY;

  // Load operators + tags + available years on mount
  useEffect(() => {
    operatorsApi.getOperators().then(res => setOperators(res.operators || [])).catch(() => {});
    tagsApi.getAllTags().then(tags => setAllTags(tags || [])).catch(() => {});
    crmApi.getAvailableYears(productCode).then(res => setAvailableYears(res.years || [])).catch(() => {});
  }, [productCode]);

  // Data loading — depends on year + archive context
  useEffect(() => {
    const timer = setTimeout(() => loadData(), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [search, statusFilter, assignedFilter, priorityFilter, productCode, selectedYear, showArchive, dateFrom, dateTo]);

  useEffect(() => {
    const interval = setInterval(() => loadData(), 60000);
    return () => clearInterval(interval);
  }, [search, statusFilter, assignedFilter, priorityFilter, productCode, selectedYear, showArchive, dateFrom, dateTo]);

  const loadData = async () => {
    try {
      const filters: Record<string, any> = { all: true, serviceType: productCode };
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      if (assignedFilter) filters.assignedTo = assignedFilter;
      if (priorityFilter) filters.leadPriority = priorityFilter;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      // Cross-year search: skip year/archive filters when searching across all years
      const currentYear = new Date().getFullYear();
      const isOldYear = selectedYear && selectedYear < currentYear;
      if (searchAllYears && search) {
        filters.is_archived = 'all';
        // no edition_year filter — search across everything
      } else {
        if (selectedYear) filters.edition_year = selectedYear;
        if (isOldYear) {
          filters.is_archived = 'all';
        } else {
          filters.is_archived = showArchive ? 'true' : 'false';
        }
      }

      const archiveFilters: Record<string, any> = { all: true, serviceType: productCode };
      if (selectedYear) archiveFilters.edition_year = selectedYear;
      if (isOldYear) {
        archiveFilters.is_archived = 'all';
      } else {
        archiveFilters.is_archived = showArchive ? 'false' : 'true'; // opposite tab count
      }

      const [appsResult, statsData, oppositeResult] = await Promise.all([
        crmApi.getApplications(filters),
        crmApi.getStats(productCode, selectedYear || undefined, isOldYear ? 'all' : (showArchive ? 'true' : 'false')),
        crmApi.getApplications({ all: true, serviceType: productCode, edition_year: selectedYear || undefined, is_archived: isOldYear ? 'all' : (showArchive ? 'false' : 'true') }),
      ]);

      // Fetch tags for all apps (single batch request)
      const apps = appsResult.applications || [];
      const appIds = apps.map((a: any) => a.id);
      try {
        const tagsMap = await tagsApi.getApplicationTagsBatch(appIds);
        apps.forEach((app: any) => { app.tags = tagsMap[app.id] || []; });
      } catch (err: any) {
        console.warn('[UniversalKanban] batch tags fetch:', err.message);
        apps.forEach((app: any) => { app.tags = []; });
      }

      setApplications(apps);
      setStats(statsData);
      setArchivedCount(showArchive ? (oppositeResult.total || oppositeResult.applications?.length || 0) : (oppositeResult.total || oppositeResult.applications?.length || 0));
      // Refresh year tabs counts
      crmApi.getAvailableYears(productCode).then(res => setAvailableYears(res.years || [])).catch(() => {});
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Błąd podczas ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appId: number, newStatus: string) => {
    try {
      await crmApi.updateProductStatus(appId, statusField, newStatus, productCode);
      setApplications(prev =>
        prev.map(app => app.id === appId ? { ...app, [statusField]: newStatus } : app)
      );
      toast.success('Status zaktualizowany');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Błąd podczas aktualizacji statusu');
      loadData();
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const blob = await crmApi.exportToExcel({ serviceType: productCode });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Wnioski_${productCode}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Eksport zakończony');
    } catch (error) {
      toast.error('Błąd podczas eksportu');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleUnarchive = async (appId: number) => {
    try {
      await crmApi.toggleArchive(appId, false);
      toast.success('Wniosek przywrócony z archiwum');
      loadData();
    } catch (error) {
      toast.error('Błąd podczas przywracania');
    }
  };

  // Filter by tag client-side (tags loaded per app)
  const filteredApplications = tagFilter
    ? applications.filter(app => app.tags?.some((t: any) => t.tag_name === tagFilter))
    : applications;

  const getAppsByStage = (stageCode: string) => {
    return filteredApplications.filter(app => {
      const appStatus = app[statusField] || app.status;
      return appStatus === stageCode;
    });
  };

  // Bulk selection handlers
  const handleToggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllVisible = () => {
    setSelectedIds(new Set(applications.map(a => a.id)));
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleToggleBulkMode = () => {
    if (bulkMode) {
      setSelectedIds(new Set());
    }
    setBulkMode(!bulkMode);
  };

  const handleBulkStatusChange = async (newStatus: string) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    try {
      const result = await crmApi.bulkStatusChange(ids, statusField, newStatus, productCode);
      setApplications(prev =>
        prev.map(app => ids.includes(app.id) ? { ...app, [statusField]: newStatus } : app)
      );
      toast.success(`Zaktualizowano ${result.updated} ${terminology.plural}`);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Bulk status change error:', error);
      toast.error('Błąd podczas zbiorczej zmiany statusu');
      loadData();
    }
  };

  const handleBulkAssign = async (operatorId: number) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    try {
      await authAxios.post('/api/operator/bulk-actions', {
        action: 'assign',
        application_ids: ids,
        data: { assigned_to: operatorId },
      });
      const op = operators.find(o => o.id === operatorId);
      setApplications(prev =>
        prev.map(app => ids.includes(app.id) ? { ...app, assigned_to: operatorId, assigned_operator_name: op ? `${op.first_name} ${op.last_name}` : '' } : app)
      );
      toast.success(`Przypisano ${ids.length} ${terminology.plural}`);
      setSelectedIds(new Set());
    } catch {
      toast.error('Błąd przypisywania');
    }
  };

  const handleBulkTag = async (tagId: number) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    try {
      const result = await tagsApi.bulkTag(ids, tagId);
      toast.success(`Otagowano ${result.updated} ${terminology.plural}`);
      loadData();
    } catch {
      toast.error('Błąd tagowania');
    }
  };

  const handleBulkEmail = async () => {
    if (selectedIds.size === 0 || !bulkEmailSubject || !bulkEmailHtml) return;
    setSendingBulkEmail(true);
    try {
      const result = await crmApi.bulkEmail(Array.from(selectedIds), bulkEmailSubject, bulkEmailHtml);
      toast.success(`Wysłano ${result.sent} emaili${result.failed ? `, ${result.failed} błędów` : ''}`);
      setShowBulkEmail(false);
      setBulkEmailSubject('');
      setBulkEmailHtml('');
    } catch {
      toast.error('Błąd wysyłania zbiorczego emaila');
    } finally {
      setSendingBulkEmail(false);
    }
  };

  // Drag handlers (disabled in archive mode)
  const handleDragStart = (appId: number) => { if (!showArchive) setDraggedApp(appId); };
  const handleDragEnd = () => { setDraggedApp(null); setDragOverStatus(null); };

  const handleDragOver = (e: React.DragEvent, stageCode: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStatus(stageCode);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverStatus(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetStage: string) => {
    e.preventDefault();
    if (draggedApp) {
      const app = applications.find(a => a.id === draggedApp);
      const currentStatus = app?.[statusField] || app?.status;

      if (app && currentStatus !== targetStage) {
        // Validate transition
        const currentStageObj = stages.find(s => s.stage_code === currentStatus);
        if (currentStageObj && currentStageObj.allowed_transitions.length > 0) {
          if (!currentStageObj.allowed_transitions.includes(targetStage)) {
            const targetLabel = stages.find(s => s.stage_code === targetStage)?.label || targetStage;
            toast.error(`Niedozwolone przejście do "${targetLabel}"`);
            setDraggedApp(null);
            setDragOverStatus(null);
            return;
          }
        }
        handleStatusChange(draggedApp, targetStage);
      }
    }
    setDraggedApp(null);
    setDragOverStatus(null);
  };

  if (loading || stagesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Ładowanie...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`bg-gradient-to-r ${gradient} text-white py-4 sm:py-5 px-3 sm:px-4 shadow-lg`}>
        <div className="max-w-full mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{productLabel}</h1>
            <p className="text-white/70 text-sm mt-0.5">
              {applications.length} {terminology.plural}{selectedYear ? ` — ${terminology.yearLabel} ${selectedYear}` : ''}{showArchive && selectedYear === currentYearConst ? ' (archiwum)' : ''}{selectedYear && selectedYear < currentYearConst ? ' (zakończony)' : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
            {/* View Toggle */}
            <div className="bg-white bg-opacity-20 rounded-lg p-1 flex gap-1">
              <button
                onClick={() => setViewMode('dashboard')}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-md font-medium transition-all flex items-center text-sm ${
                  viewMode === 'dashboard' ? 'bg-white text-gray-800 shadow-md' : 'text-white hover:bg-white/10'
                }`}
              >
                <BarChart3 size={16} /> <span className="hidden sm:inline ml-1.5">Dashboard</span>
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-md font-medium transition-all flex items-center text-sm ${
                  viewMode === 'kanban' ? 'bg-white text-gray-800 shadow-md' : 'text-white hover:bg-white/10'
                }`}
              >
                <Columns3 size={16} /> <span className="hidden sm:inline ml-1.5">Kanban</span>
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-md font-medium transition-all flex items-center text-sm ${
                  viewMode === 'calendar' ? 'bg-white text-gray-800 shadow-md' : 'text-white hover:bg-white/10'
                }`}
              >
                <Calendar size={16} /> <span className="hidden sm:inline ml-1.5">Kalendarz</span>
              </button>
            </div>

            <button
              onClick={() => {
                if (HAS_DEDICATED_FORM.includes(productCode)) {
                  navigate(applyRoute);
                } else {
                  setShowQuickAdd(true);
                }
              }}
              className="bg-white text-gray-800 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center shadow-lg text-sm"
            >
              <Plus size={18} /> <span className="hidden sm:inline ml-1.5">{terminology.newItem}</span>
            </button>
            <button
              onClick={() => setShowAIAssistant(true)}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-colors flex items-center shadow-lg text-sm"
            >
              <MessageSquare size={18} /> <span className="hidden sm:inline ml-1.5">AI</span>
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exportingExcel}
              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-colors flex items-center shadow-lg disabled:opacity-50 text-sm"
            >
              <FileSpreadsheet size={18} />
              <span className="hidden sm:inline ml-1.5">{exportingExcel ? 'Eksport...' : 'Excel'}</span>
            </button>
            <button
              type="button"
              onClick={handleToggleBulkMode}
              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-semibold transition-colors flex items-center shadow-lg text-sm ${
                bulkMode
                  ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-300'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {bulkMode ? <CheckSquare size={18} /> : <Square size={18} />}
              <span className="hidden sm:inline ml-1.5">{bulkMode ? 'Zaznaczanie' : 'Zaznacz'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && viewMode === 'kanban' && (
        <div className="max-w-full mx-auto px-4 py-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Łącznie</p>
                  <p className="text-2xl font-bold">{filteredApplications.length}</p>
                </div>
                <TrendingUp className="text-blue-500" size={32} />
              </div>
            </div>
            {isFinancialProduct ? (
              <>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Z ostrzeżeniami</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {filteredApplications.filter(a => a.warnings?.length > 0).length}
                      </p>
                    </div>
                    <AlertTriangle className="text-yellow-500" size={32} />
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div>
                    <p className="text-sm text-gray-600">Średnia kwota</p>
                    <p className="text-2xl font-bold">
                      {filteredApplications.length > 0
                        ? Math.round(filteredApplications.reduce((s, a) => s + (Number(isLoanProduct ? a.loan_amount : a.requested_amount) || 0), 0) / filteredApplications.length).toLocaleString('pl-PL')
                        : 0} zł
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Nowe (7 dni)</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {filteredApplications.filter(a => {
                          const d = new Date(a.created_at);
                          return d >= new Date(Date.now() - 7 * 86400000);
                        }).length}
                      </p>
                    </div>
                    <Calendar className="text-blue-500" size={32} />
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">W trakcie</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {filteredApplications.filter(a => {
                          const st = a.trip_status || a.status;
                          return st && !['zakonczony', 'anulowany', 'odrzućony', 'completed', 'cancelled', 'rejected'].includes(st);
                        }).length}
                      </p>
                    </div>
                    <Clock className="text-purple-500" size={32} />
                  </div>
                </div>
              </>
            )}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{isFinancialProduct ? 'Sukces' : 'Zakończone'}</p>
                  <p className="text-2xl font-bold text-green-600">
                    {getAppsByStage(stages.find(s => s.is_success)?.stage_code || 'success').length}
                  </p>
                </div>
                <CheckCircle className="text-green-500" size={32} />
              </div>
            </div>
          </div>

          {/* Pipeline funnel — conversion per stage */}
          {stages.length > 1 && filteredApplications.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-medium text-gray-500 mb-2">{terminology.pipeline}</p>
              <div className="flex items-end gap-1">
                {stages.map((stage, i) => {
                  const count = getAppsByStage(stage.stage_code).length;
                  const pct = filteredApplications.length > 0 ? (count / filteredApplications.length) * 100 : 0;
                  const barH = Math.max(pct, 4);
                  const isTerminalFail = stage.is_terminal && !stage.is_success;
                  const colors = ['bg-blue-400', 'bg-indigo-400', 'bg-violet-400', 'bg-purple-400', 'bg-fuchsia-400', 'bg-pink-400', 'bg-amber-400', 'bg-emerald-500', 'bg-red-400', 'bg-cyan-400', 'bg-teal-400', 'bg-orange-400'];
                  return (
                    <div key={stage.stage_code} className="flex-1 flex flex-col items-center" title={`${stage.label}: ${count} (${Math.round(pct)}%)`}>
                      <span className="text-[10px] font-bold text-gray-700 mb-0.5">{count}</span>
                      <div className={`w-full rounded-t ${stage.is_success ? 'bg-emerald-500' : isTerminalFail ? 'bg-red-400' : colors[i % colors.length]}`}
                        style={{ height: `${barH}px`, minHeight: '4px', maxHeight: '48px' }} />
                      <span className="text-[9px] text-gray-400 mt-1 truncate w-full text-center">{stage.label.length > 10 ? stage.label.slice(0, 8) + '..' : stage.label}</span>
                    </div>
                  );
                })}
              </div>
              {(() => {
                const successStage = stages.find(s => s.is_success);
                const failStage = stages.find(s => s.is_terminal && !s.is_success);
                const won = successStage ? getAppsByStage(successStage.stage_code).length : 0;
                const lost = failStage ? getAppsByStage(failStage.stage_code).length : 0;
                const decided = won + lost;
                const rate = decided > 0 ? Math.round((won / decided) * 100) : 0;
                return decided > 0 ? (
                  <p className="text-[10px] text-gray-400 mt-2 text-right">
                    {terminology.winLabel}: <span className="font-bold text-green-600">{rate}%</span> ({won}/{decided})
                  </p>
                ) : null;
              })()}
            </div>
          )}

          {/* SLA Health + Lead Priority mini bar */}
          {filteredApplications.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {/* SLA Health */}
              {(() => {
                const breach = filteredApplications.filter(a => a.sla_status === 'breach').length;
                const warning = filteredApplications.filter(a => a.sla_status === 'warning').length;
                const ok = filteredApplications.length - breach - warning;
                return (breach > 0 || warning > 0) ? (
                  <div className="bg-white rounded-lg shadow px-4 py-2 flex items-center gap-3 text-xs">
                    <span className="font-medium text-gray-500">SLA:</span>
                    {breach > 0 && <span className="flex items-center gap-1 text-red-600 font-bold"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />{breach} przekroczone</span>}
                    {warning > 0 && <span className="flex items-center gap-1 text-amber-600 font-semibold"><span className="w-2 h-2 rounded-full bg-amber-400" />{warning} ostrzeżeń</span>}
                    <span className="text-green-600">{ok} OK</span>
                  </div>
                ) : null;
              })()}
              {/* Lead Priority */}
              {(() => {
                const hot = filteredApplications.filter(a => a.lead_priority === 'hot').length;
                const warm = filteredApplications.filter(a => a.lead_priority === 'warm').length;
                const cold = filteredApplications.filter(a => a.lead_priority === 'cold').length;
                return (hot > 0 || warm > 0) ? (
                  <div className="bg-white rounded-lg shadow px-4 py-2 flex items-center gap-3 text-xs">
                    <span className="font-medium text-gray-500">Priorytety:</span>
                    {hot > 0 && <span className="text-red-600 font-bold">🔥 {hot} hot</span>}
                    {warm > 0 && <span className="text-amber-600 font-semibold">☀️ {warm} warm</span>}
                    {cold > 0 && <span className="text-gray-500">{cold} cold</span>}
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>
      )}

      {/* Year Tabs — izolacja naborów per rok */}
      {viewMode === 'kanban' && (
        <div className="max-w-full mx-auto px-3 sm:px-4 pt-2 pb-1">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {/* Bieżące (current year, non-archived) */}
            {(() => {
              const currentYearData = availableYears.find(y => y.year === currentYearConst);
              const activeCount = currentYearData?.active ?? 0;
              const archivedCurrentYear = currentYearData?.archived ?? 0;
              const pastYears = availableYears.filter(y => y.year < currentYearConst);
              return (
                <>
                  <button
                    type="button"
                    onClick={() => { setSelectedYear(currentYearConst); setShowArchive(false); }}
                    className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 border-b-2 whitespace-nowrap ${
                      selectedYear === currentYearConst && !showArchive
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Columns3 size={15} />
                    Bieżące ({selectedYear === currentYearConst && !showArchive ? applications.length : activeCount})
                  </button>
                  {/* Archiwum bieżącego roku — tylko jeśli są zarchiwizowane */}
                  {archivedCurrentYear > 0 && (
                    <button
                      type="button"
                      onClick={() => { setSelectedYear(currentYearConst); setShowArchive(true); }}
                      className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 border-b-2 whitespace-nowrap ${
                        selectedYear === currentYearConst && showArchive
                          ? 'border-amber-500 text-amber-700'
                          : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Archive size={15} />
                      Archiwum {currentYearConst} ({archivedCurrentYear})
                    </button>
                  )}
                  {/* Poprzednie lata — dynamicznie, tylko te w których są dane */}
                  {pastYears.map(yearData => (
                    <button
                      key={yearData.year}
                      type="button"
                      onClick={() => { setSelectedYear(yearData.year); setShowArchive(false); }}
                      className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 border-b-2 whitespace-nowrap ${
                        selectedYear === yearData.year
                          ? 'border-gray-500 text-gray-700 bg-gray-50'
                          : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Archive size={14} className="opacity-50" />
                      {yearData.year} ({yearData.total})
                    </button>
                  ))}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Archive Summary — show when viewing archived/past year data */}
      {viewMode === 'kanban' && (showArchive || selectedYear < currentYearConst) && (
        <div className="max-w-full mx-auto px-3 sm:px-4 pt-1">
          <ArchiveSummary productCode={productCode} selectedYear={selectedYear} />
        </div>
      )}

      {/* Search + Filters */}
      {viewMode === 'kanban' && (
        <div className="max-w-full mx-auto px-3 sm:px-4 pb-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={searchAllYears ? "Szukaj we wszystkich latach..." : "Szukaj po imieniu, nazwisku lub ID..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer whitespace-nowrap select-none">
              <input
                type="checkbox"
                checked={searchAllYears}
                onChange={(e) => setSearchAllYears(e.target.checked)}
                className="rounded border-gray-300"
              />
              Wszystkie lata
            </label>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filtruj po statusie"
                className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 bg-white flex-1 sm:flex-none sm:min-w-[180px]"
              >
                <option value="">Wszystkie statusy</option>
                {stages.map(s => (
                  <option key={s.stage_code} value={s.stage_code}>{s.label}</option>
                ))}
              </select>
              <select
                aria-label="Filtruj wg operatora"
                value={assignedFilter}
                onChange={(e) => setAssignedFilter(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="">Wszyscy operatorzy</option>
                <option value="me">Moje wnioski</option>
                <option value="unassigned">Nieprzydzielone</option>
                {operators.map(op => (
                  <option key={op.id} value={String(op.id)}>{op.name}</option>
                ))}
              </select>
              <select
                aria-label="Filtruj wg priorytetu"
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="">Wszystkie priorytety</option>
                <option value="hot">Hot</option>
                <option value="warm">Warm</option>
                <option value="cold">Cold</option>
              </select>
              {/* Tag filter (client-side) */}
              {(() => {
                const allTags = new Map<string, string>();
                applications.forEach(app => app.tags?.forEach((t: any) => allTags.set(t.tag_name, t.tag_color)));
                return allTags.size > 0 ? (
                  <select
                    aria-label="Filtruj wg taga"
                    value={tagFilter}
                    onChange={(e) => setTagFilter(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">Wszystkie tagi</option>
                    {Array.from(allTags.entries()).map(([name]) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                ) : null;
              })()}
              {/* Date range filter */}
              <div className="hidden sm:flex items-center gap-1">
                <CalendarRange size={14} className="text-gray-400" />
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none w-32" title="Od daty" />
                <span className="text-gray-400 text-xs">-</span>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none w-32" title="Do daty" />
              </div>
              <button
                type="button"
                onClick={() => setShowWipConfig(!showWipConfig)}
                className={`px-2 py-1.5 text-sm border rounded-lg transition-colors flex items-center gap-1 flex-shrink-0 ${
                  showWipConfig ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-200 text-gray-500 hover:text-amber-600 hover:border-amber-300'
                }`}
                title="Limity WIP na kolumnach"
              >
                <AlertTriangle size={14} /> <span className="hidden sm:inline">WIP</span>
              </button>
              {(search || statusFilter || assignedFilter || priorityFilter || tagFilter || dateFrom || dateTo) && (
                <>
                  <button
                    type="button"
                    onClick={() => { setSearch(''); setStatusFilter(''); setAssignedFilter(''); setPriorityFilter(''); setTagFilter(''); setDateFrom(''); setDateTo(''); }}
                    className="px-3 py-2 text-sm text-gray-500 hover:text-red-500 border rounded-lg hover:border-red-300 transition-colors flex items-center gap-1 flex-shrink-0"
                  >
                    <X size={14} /> <span className="hidden sm:inline">Wyczyść</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSaveView(!showSaveView)}
                    className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-1 flex-shrink-0"
                    title="Zapisz widok"
                  >
                    <Bookmark size={14} /> <span className="hidden sm:inline">Zapisz</span>
                  </button>
                </>
              )}
            </div>
            {/* Saved views bar */}
            {(savedViews.length > 0 || showSaveView) && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {savedViews.map(v => (
                  <div key={v.id || v.name} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full px-3 py-1 text-xs">
                    <button type="button" onClick={() => handleLoadView(v)} className="text-blue-700 font-medium hover:text-blue-900">
                      {v.is_shared && <Share2 size={10} className="inline mr-1 text-blue-400" />}{v.name}
                    </button>
                    <button type="button" onClick={() => handleDeleteView(v.id, v.name)} className="text-blue-400 hover:text-red-500 ml-0.5">
                      <X size={10} />
                    </button>
                  </div>
                ))}
                {showSaveView && (
                  <div className="inline-flex items-center gap-1">
                    <input
                      type="text"
                      value={viewName}
                      onChange={e => setViewName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveView(); if (e.key === 'Escape') setShowSaveView(false); }}
                      placeholder="Nazwa widoku..."
                      className="border rounded px-2 py-1 text-xs w-32 focus:ring-1 focus:ring-blue-400"
                      autoFocus
                    />
                    <button type="button" onClick={handleSaveView} disabled={!viewName.trim()}
                      className="text-xs text-blue-600 font-medium hover:text-blue-800 disabled:opacity-30">Zapisz</button>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* WIP Limits Config */}
          {showWipConfig && (
            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-amber-600" />
                <span className="text-xs font-medium text-amber-800">Limity WIP (Work In Progress) — 0 = brak limitu</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {stages.map(s => (
                  <div key={s.stage_code} className="flex items-center gap-1.5 bg-white border rounded px-2 py-1">
                    <div className={`w-2 h-2 rounded-full ${s.color}`} />
                    <span className="text-xs text-gray-600 truncate max-w-[100px]">{s.label}</span>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={wipLimits[s.stage_code] || ''}
                      onChange={e => updateWipLimit(s.stage_code, parseInt(e.target.value) || 0)}
                      placeholder="0"
                      className="w-12 text-xs text-center border rounded px-1 py-0.5 focus:ring-1 focus:ring-amber-400"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Action Toolbar */}
      {bulkMode && viewMode === 'kanban' && (
        <div className="max-w-full mx-auto px-3 sm:px-4 pb-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-blue-800">
              Zaznaczono: {selectedIds.size} / {applications.length}
            </span>
            <button
              type="button"
              onClick={handleSelectAllVisible}
              className="text-xs text-blue-700 hover:text-blue-900 underline"
            >
              Zaznacz wszystkie
            </button>
            <button
              type="button"
              onClick={handleDeselectAll}
              className="text-xs text-blue-700 hover:text-blue-900 underline"
            >
              Odznacz
            </button>
            {selectedIds.size > 0 && (
              <>
                <span className="text-gray-300">|</span>
                <span className="text-xs text-gray-600">Zmień status na:</span>
                {stages.map(s => (
                  <button
                    key={s.stage_code}
                    type="button"
                    onClick={() => handleBulkStatusChange(s.stage_code)}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  >
                    <div className={`w-2 h-2 rounded-full ${s.color}`} />
                    {s.label}
                  </button>
                ))}
                <span className="text-gray-300">|</span>
                <div className="inline-flex items-center gap-1.5">
                  <UserPlus size={14} className="text-gray-500" />
                  <select
                    className="text-xs border rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-blue-400"
                    defaultValue=""
                    onChange={e => { if (e.target.value) { handleBulkAssign(Number(e.target.value)); e.target.value = ''; } }}
                  >
                    <option value="">Przypisz do...</option>
                    {operators.map(op => (
                      <option key={op.id} value={op.id}>{op.first_name} {op.last_name}</option>
                    ))}
                  </select>
                </div>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={() => setShowBulkEmail(true)}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                >
                  <Mail size={14} className="text-gray-500" />
                  Email ({selectedIds.size})
                </button>
                <button
                  type="button"
                  onClick={() => setShowBulkSms(true)}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-white border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors"
                >
                  <Smartphone size={14} className="text-gray-500" />
                  SMS ({selectedIds.size})
                </button>
                {allTags.length > 0 && (
                  <>
                    <span className="text-gray-300">|</span>
                    <div className="inline-flex items-center gap-1.5">
                      <Tag size={14} className="text-gray-500" />
                      <select
                        className="text-xs border rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-blue-400"
                        defaultValue=""
                        onChange={e => { if (e.target.value) { handleBulkTag(Number(e.target.value)); e.target.value = ''; } }}
                      >
                        <option value="">Dodaj tag...</option>
                        {allTags.map((tag: any) => (
                          <option key={tag.id} value={tag.id}>{tag.tag_name || tag.name}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Email Modal — DISABLED for GDPR safety */}
      {showBulkEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Mail size={20} className="text-red-600" />
                Zbiorczy email
              </h3>
            </div>
            <div className="p-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <strong>Masowa wysyłka wyłączona</strong> — ze względów bezpieczeństwa RODO wysyłka zbiorcza jest niedostępna.
                Emaile można wysyłać pojedynczo z widoku szczegółów wniosku.
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex justify-end">
              <button onClick={() => setShowBulkEmail(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk SMS Modal */}
      {showBulkSms && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Smartphone size={20} className="text-green-600" />
                Zbiorczy SMS do {selectedIds.size} odbiorców
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Zmieńne: {'{{first_name}}'}, {'{{last_name}}'}. Max 480 znaków.
              </p>
            </div>
            <div className="p-6">
              <textarea
                value={bulkSmsMessage}
                onChange={e => setBulkSmsMessage(e.target.value)}
                rows={5}
                maxLength={480}
                placeholder="Szanowny/a {{first_name}}, prosimy o kontakt w sprawie wniosku. Fundacja NH: 575 661 551"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
              />
              <div className="text-right text-xs text-gray-400 mt-1">{bulkSmsMessage.length}/480</div>
            </div>
            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex justify-between">
              <button onClick={() => setShowBulkSms(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
                Anuluj
              </button>
              <button
                onClick={async () => {
                  if (!bulkSmsMessage.trim()) return;
                  setSendingBulkSms(true);
                  try {
                    const result = await crmApi.bulkSms(Array.from(selectedIds), bulkSmsMessage);
                    toast.success(`SMS: ${result.sent} wysłanych, ${result.failed} błędów${result.noPhone ? `, ${result.noPhone} bez telefonu` : ''}`);
                    setShowBulkSms(false);
                    setBulkSmsMessage('');
                  } catch { toast.error('Błąd wysyłania SMS'); }
                  finally { setSendingBulkSms(false); }
                }}
                disabled={sendingBulkSms || !bulkSmsMessage.trim()}
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                <Send size={14} />
                {sendingBulkSms ? 'Wysyłanie...' : 'Wyślij SMS'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {viewMode === 'dashboard' ? (
        <div className="max-w-full mx-auto px-4 pb-8">
          <CRMDashboard productCode={productCode} />
        </div>
      ) : viewMode === 'calendar' ? (
        <div className="max-w-full mx-auto px-4 pb-8">
          <MeetingsCalendar productCode={productCode} />
        </div>
      ) : showArchive ? (
        /* Archive: table view (no drag-and-drop) */
        <div className="max-w-full mx-auto px-3 sm:px-4 pb-8">
          {applications.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Archive size={48} className="mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">{terminology.archiveEmpty}</p>
              <p className="text-sm mt-1">{terminology.archiveHint}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">ID</th>
                    <th className="px-4 py-3 text-left font-medium">Imię i nazwisko</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Rok</th>
                    <th className="px-4 py-3 text-left font-medium">Data</th>
                    <th className="px-4 py-3 text-right font-medium">Akcje</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {applications.map(app => {
                    const appStatus = app[statusField] || app.status;
                    const stageObj = stages.find(s => s.stage_code === appStatus);
                    return (
                      <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">{app.submission_id || `#${app.id}`}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => navigate(`${programConfig?.routeBase || '/crm'}/application/${app.id}`)}
                            className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {app.first_name} {app.last_name}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${stageObj?.color || 'bg-gray-100'} bg-opacity-20`}>
                            <span className={`w-2 h-2 rounded-full ${stageObj?.color || 'bg-gray-400'}`} />
                            {stageObj?.label || appStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{app.edition_year || '—'}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {app.created_at ? new Date(app.created_at).toLocaleDateString('pl-PL') : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => handleUnarchive(app.id)}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md text-blue-600 hover:bg-blue-50 border border-blue-200 transition-colors"
                            title="Przywróć z archiwum"
                          >
                            <RotateCcw size={13} />
                            Przywróć
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="max-w-full mx-auto px-2 sm:px-4 pb-8">
          {isMobile ? (
            <KanbanListView
              stages={stages}
              getAppsByStage={getAppsByStage}
              statusField={statusField}
              showLoanAmount={isLoanProduct}
              onCardClick={(id) => navigate(`${programConfig?.routeBase || '/crm'}/application/${id}`)}
              onStatusChange={handleStatusChange}
              bulkMode={bulkMode}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              terminology={terminology}
            />
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-4">
              {stages.map(stage => (
                <KanbanColumn
                  key={stage.stage_code}
                  stage={stage}
                  apps={getAppsByStage(stage.stage_code)}
                  statusField={statusField}
                  showLoanAmount={isLoanProduct}
                  dragOverStatus={dragOverStatus}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onCardClick={(id) => navigate(`${programConfig?.routeBase || '/crm'}/application/${id}`)}
                  stages={stages}
                  onStatusChange={handleStatusChange}
                  bulkMode={bulkMode}
                  selectedIds={selectedIds}
                  onToggleSelect={handleToggleSelect}
                  wipLimit={wipLimits[stage.stage_code]}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* AI Assistant Modal */}
      {showAIAssistant && (
        <AIAssistantModal isOpen={showAIAssistant} onClose={() => setShowAIAssistant(false)} />
      )}

      {/* Quick Add Application Modal */}
      {showQuickAdd && (
        <QuickAddApplicationModal
          productCode={productCode}
          productLabel={productLabel}
          terminology={terminology}
          onClose={() => setShowQuickAdd(false)}
          onCreated={() => { setShowQuickAdd(false); loadData(); }}
        />
      )}
    </div>
  );
};
